import { React, serviceClient, dtos, observer, shared, observable, ui, Link, selectFilter, _, splitIntoWords, uuid, moveArray, removeArray, makeObservable, withRouter, cache, getEndpointIcon } from "../../common";
import ValueEditor from "../../flows/value-editor";
import PageTitle from "../../page-title";
import TagsEditor from "../../tags-editor";
import AutoComplete from "../../autocomplete";
import Sessions from "../../system/sessions";
import Notifications from "../../system/notifications";
import CustomersIndex from ".";
import BillingSettings from "../../accounts/billing";
import { Space } from "antd";
import { AISessionActionInfo } from "../../dtos";
import moment from "moment";

@observer
class AISessionDetails extends React.Component {
    @observable purpose = "";
    @observable id = "";
    @observable editPurpose = "";
    @observable tab = "actions";
    @observable loading = true;
    @observable refreshing = true;
    @observable mcpUrl = "";
    @observable actions: AISessionActionInfo[] = [];
    @observable canApply = false;
    @observable actionToShow: AISessionActionInfo = null;
    @observable logToShow: string = null;
    @observable log = "";

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.refresh((this.props as any).params.id);
    }

    componentDidUpdate(prevProps) {
        var newId = (this.props as any).params.id;
        if (newId != prevProps.params.id) {
            this.refresh(newId);
            this.tab = "actions";
        }
    }

    async refresh(id) {
        this.refreshing = true;
        let session = (await serviceClient.get(new dtos.ListAISessions({ specificIds: [id] }))).items[0];
        let ss = await cache.getSystemSettings();
        this.id = session.id;
        this.purpose = session.purpose;
        this.log = session.log;
        this.editPurpose = session.purpose;
        const serviceUrl = new URL(serviceClient.baseUrl);
        this.mcpUrl = `${serviceUrl.protocol}//${serviceUrl.hostname}/mcp/${this.id}`;
        this.actions = session.actions;
        this.canApply = this.actions.length > 0;
        if (this.actions.length === 0) {
            this.actions = [{
                date: "",
                id: "",
                functionName: "No actions in this session",
                arguments: "",
                description: ""
            }]
        }
        this.loading = false;
        this.refreshing = false;
    }

    showAction(action: AISessionActionInfo) {
        this.actionToShow = action;
    }

    async patchSession() {
        let updated = await serviceClient.patch(new dtos.PatchAISession({
            sessionId: this.id,
            purpose: this.editPurpose
        }));
        this.purpose = this.editPurpose;
        ui.message.success("Session updated");
    }

    async deleteSession() {
        await serviceClient.delete(new dtos.DeleteAISession({ sessionId: this.id }));
        ui.message.success("Session deleted");
        window.location.hash = "/ai/sessions";
    }

    async deleteAction(id: string) {
        await serviceClient.delete(new dtos.DeleteAISessionAction({ sessionId: this.id, actionId: id }));
        ui.message.success("Action deleted");
        this.refresh(this.id);
    }

    async clearActions() {
        await serviceClient.delete(new dtos.ClearAISessionActions({ sessionId: this.id }));
        ui.message.success("Actions cleared");
        this.refresh(this.id);
    }

    async applyChanges() {
        const updatedSession = await serviceClient.post(new dtos.ApplyAISession({ sessionId: this.id }));
        ui.message.success("Changes applied");
        this.refresh(this.id);
    }

    copyToClipboard(url: string) {
        navigator.clipboard.writeText(url);
        ui.message.success("Copied to clipboard");
    }

    render() {
        return (
            <div>
                <PageTitle title={this.purpose} />
                <ui.Breadcrumb>
                    <ui.Breadcrumb.Item><Link to="/ai/sessions">AI Sessions</Link></ui.Breadcrumb.Item>
                </ui.Breadcrumb>
                {!shared.collapsed && <ui.Typography.Title><i className="fa fa-message-bot" /> {this.purpose}</ui.Typography.Title>}
                {this.loading && <div style={{ textAlign: "center", paddingTop: 100 }}><ui.Spin size="default" /></div>}
                {!this.loading && <ui.Tabs activeKey={this.tab} onChange={t => this.tab = t}>
                    <ui.Tabs.TabPane key="actions" tab="Changes">
                        <ui.Table loading={this.refreshing} dataSource={this.actions} pagination={{
                            showTotal: () => <ui.Space>
                                {this.canApply && <ui.Popconfirm onConfirm={() => this.applyChanges()} title="Apply Changes?"><ui.Button type="primary">Apply Changes</ui.Button></ui.Popconfirm>}
                                {this.canApply && <ui.Popconfirm onConfirm={() => this.clearActions()} title="Clear all changes?"><ui.Button danger type="ghost">Clear Changes</ui.Button></ui.Popconfirm>}
                                <ui.Button onClick={() => this.refresh(this.id)}><i className="fa fa-refresh" /></ui.Button>
                                {this.log && <ui.Button onClick={() => this.logToShow = this.log}>View Log</ui.Button>}
                            </ui.Space>
                        }}>
                            <ui.Table.Column title="Date" render={(v: AISessionActionInfo) => v.id ? moment(v.date).format("LLL") : ""} />
                            <ui.Table.Column title="Action" render={(v: AISessionActionInfo) => v.id ? <ui.Button type="link" onClick={() => this.showAction(v)}>{splitIntoWords(v.functionName)}</ui.Button> : v.functionName} />
                            <ui.Table.Column title="Changes" render={(v: AISessionActionInfo) => <div style={{ fontSize: ".9em", whiteSpace: "pre", wordWrap: "break-word", wordBreak: "break-word", maxHeight: "200px", overflowY: "hidden", textOverflow: "ellipsis" }}>{v.description}</div>} />
                            <ui.Table.Column render={(v: AISessionActionInfo) => <ui.Button.Group>
                                {<ui.Popconfirm placement="left" onConfirm={() => this.deleteAction(v.id)} title="Remove this action?"><ui.Button size="small"><i className="fa fa-trash" /></ui.Button></ui.Popconfirm>}
                            </ui.Button.Group>} />
                        </ui.Table>
                    </ui.Tabs.TabPane>
                    <ui.Tabs.TabPane key="general" tab="Settings">
                        <ui.Form layout="vertical" onSubmitCapture={() => this.patchSession()}>
                            <ui.Row gutter={8}>
                                <ui.Col span={12}>
                                    <ui.Form.Item label="URL">
                                        <ui.Input disabled readOnly value={this.mcpUrl} />
                                        <ui.Button onClick={() => this.copyToClipboard(this.mcpUrl)} size="small" type="link">Copy to Clipboard</ui.Button>
                                    </ui.Form.Item>
                                    <ui.Form.Item label="Purpose">
                                        <ui.Input value={this.editPurpose} onChange={v => this.editPurpose = v.target.value} />
                                    </ui.Form.Item>
                                </ui.Col>
                            </ui.Row>
                            <ui.Form.Item>
                                <Space>
                                    {shared.canWrite() && <ui.Button htmlType="submit" type="primary">Save</ui.Button>}
                                    {shared.canDelete() && <ui.Popconfirm title="Are you sure you want to delete this session?" onConfirm={() => this.deleteSession()}><a>Delete Session</a></ui.Popconfirm>}
                                </Space>
                            </ui.Form.Item>
                        </ui.Form>
                    </ui.Tabs.TabPane>
                </ui.Tabs>}
                <ui.Modal width={"50%"} cancelText="Close" okButtonProps={{ hidden: true }} open={this.actionToShow != null} onCancel={() => this.actionToShow = null} title={splitIntoWords(this.actionToShow?.functionName)}>
                    <ui.Form layout="vertical">
                        <ui.Form.Item label="Arguments:">
                            <pre style={{ color: "#999" }}>{this.actionToShow?.arguments}</pre>
                        </ui.Form.Item>
                        <ui.Form.Item label="Changes that will occur:">
                            <pre style={{ color: "#999", maxHeight: "150px", overflowY: "auto" }}>
                                {this.actionToShow?.description}
                            </pre>
                        </ui.Form.Item>
                    </ui.Form>
                </ui.Modal>
                <ui.Modal title="Session Log" width={"50%"} cancelText="Close" okButtonProps={{ hidden: true }} open={this.logToShow != null} onCancel={() => this.logToShow = null}>
                    <p>This log shows the entire history of changes in this session, scroll to bottom for most recent changes</p>
                    <pre style={{ color: "#999", maxHeight: "300px", overflowY: "auto" }}>
                        {this.logToShow}
                    </pre>
                </ui.Modal>
            </div>
        );
    }
}

export default withRouter(AISessionDetails);